'use client'

/**
 * A live summary's topics and their cited lines — the one rendering of a
 * `LiveSummarySnapshot`'s content (#14379), shared by the recording bar's
 * live panel and the note page's "Live draft" (#14479), so the two never
 * drift into two readings of the same summary.
 *
 * Closed topics are quiet and the open one is marked In progress; each line
 * is shown as written and carries a citation control, a real button named
 * for what it opens, that hands its claim id to `onOpenCitation`. Where the
 * cited spans are drawn is the caller's: `controlsId` names that region.
 *
 * {@link LiveTranscriptSegmentBody} is one finalized span as both draw it —
 * its time and its words, never a speaker label.
 */

import type { ReactNode } from 'react'

import { Quote } from '@/components/icons'
import { formatTimecode } from '@/lib/meetings/record-page-model'
import type { LiveClaim, LiveTopic } from '@/lib/notetaker/live-summary-fold'
import type { LiveTranscriptSegment } from '@/lib/notetaker/live-transcript-view'
import { cn } from '@/lib/utils'

/** One line, with the topic it sits in — what a line slot is handed. */
export interface LiveSummaryLine {
  topic: LiveTopic
  claim: LiveClaim
}

export interface LiveSummaryTopicListProps {
  topics: readonly LiveTopic[]
  /** The line whose citation is open, or null. */
  activeClaimId: string | null
  onOpenCitation: (claimId: string) => void
  /** The id of the region the cited spans are drawn in. */
  controlsId: string
  /** The caller's focus ring for the citation control. */
  focusClassName: string
  /** Extra controls at the end of each line (#14382's flag-as-wrong). */
  renderLineActions?: (line: LiveSummaryLine) => ReactNode
}

export function LiveSummaryTopicList({
  topics,
  activeClaimId,
  onOpenCitation,
  controlsId,
  focusClassName,
  renderLineActions,
}: LiveSummaryTopicListProps) {
  return (
    <div className="flex flex-col gap-4">
      {topics.map(topic => (
        <section
          key={topic.id}
          data-testid="live-summary-topic"
          data-status={topic.status}
          aria-label={topic.heading}
          className="flex flex-col gap-2"
        >
          <h3
            className={cn(
              'flex flex-wrap items-center gap-2 text-sm font-semibold',
              topic.status === 'closed'
                ? 'text-text-secondary'
                : 'text-text-primary',
            )}
          >
            {topic.heading}
            {topic.status === 'open' ? (
              <span
                data-testid="live-summary-topic-in-progress"
                className="rounded-full bg-fill-10 px-2 py-0.5 text-xs font-medium text-text-secondary"
              >
                In progress
              </span>
            ) : null}
          </h3>
          <ul className="flex flex-col gap-1">
            {topic.claims.map(claim => (
              <li
                key={claim.id}
                data-testid="live-summary-line"
                className={cn(
                  'flex items-start gap-1 text-sm',
                  topic.status === 'closed'
                    ? 'text-text-secondary'
                    : 'text-text-primary',
                )}
              >
                <span className="flex-1 py-3">{claim.text}</span>
                <button
                  type="button"
                  data-testid="live-summary-line-citation"
                  aria-label={`Show where this was said: ${claim.text}`}
                  aria-pressed={activeClaimId === claim.id}
                  aria-controls={controlsId}
                  onClick={() => onOpenCitation(claim.id)}
                  className={cn(
                    'inline-flex size-8 pointer-coarse:min-h-11 pointer-coarse:min-w-11 shrink-0 items-center justify-center rounded-md',
                    'text-text-secondary hover:bg-surface-panel hover:text-text-primary',
                    focusClassName,
                    activeClaimId === claim.id &&
                      'bg-surface-panel text-text-primary',
                  )}
                >
                  <Quote className="h-4 w-4" aria-hidden="true" />
                </button>
                {renderLineActions?.({ topic, claim })}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

/** One finalized span: its time and its text. Never a speaker label. */
export function LiveTranscriptSegmentBody({
  segment,
  cited,
}: {
  segment: LiveTranscriptSegment
  cited: boolean
}) {
  return (
    <>
      <span className="shrink-0 pt-0.5 font-mono text-xs tabular-nums text-text-secondary">
        {formatTimecode(segment.startMs)}
      </span>
      <p className="text-sm leading-relaxed text-text-primary">
        {cited ? <span className="sr-only">Cited: </span> : null}
        {segment.text}
      </p>
    </>
  )
}
